import { Card, Text, Box, Stack } from '@mantine/core';
import type {
  AvailabilityRequestWithDetails,
  SlotAvailabilityMap,
  SlotAvailabilityStatus,
  SlotConflict,
} from '../../../services/api/coachPortal';
import { TimeSlotCard } from './TimeSlotCard';

export interface TimeSlotAvailabilitySectionProps {
  timeSlots: AvailabilityRequestWithDetails['time_slots'];
  slotAvailability: SlotAvailabilityMap;
  onChange: (slotId: string, status: SlotAvailabilityStatus, conflicts?: SlotConflict[]) => void;
  perspective?: 'coach' | 'cm';
  // MED-1026: group-coaching session duration (minutes) used to render each
  // slot's time window. Threaded down to every TimeSlotCard.
  durationMinutes: number;
}

export function TimeSlotAvailabilitySection({
  timeSlots,
  slotAvailability,
  onChange,
  perspective = 'coach',
  durationMinutes,
}: TimeSlotAvailabilitySectionProps) {
  const isCm = perspective === 'cm';

  // Calculate availability summary
  const availableCount = Object.values(slotAvailability).filter(
    s => s.status === 'full' || s.status === 'partial',
  ).length;
  const totalCount = timeSlots.length;

  return (
    <Card withBorder padding="xl" radius="md">
      <Text fw={600} mb="xs">
        Time Slot Availability
      </Text>
      <Text size="sm" c="dimmed" mb="md">
        {isCm
          ? "For each time slot below, indicate the coach's availability. If partially available, add the specific conflicting dates."
          : "For each time slot below, indicate your availability. If you're partially available, add the specific dates you have conflicts."}
      </Text>

      {availableCount > 0 && (
        <Box
          mb="md"
          p="sm"
          style={{
            background: 'var(--mantine-color-green-0)',
            borderRadius: 'var(--mantine-radius-md)',
          }}
        >
          <Text size="sm">
            Available for{' '}
            <Text component="span" fw={600} c="green">
              {availableCount}
            </Text>{' '}
            of {totalCount} time slots
          </Text>
        </Box>
      )}

      <Stack gap="sm">
        {timeSlots.map(slot => (
          <TimeSlotCard
            key={slot.id}
            slot={slot}
            durationMinutes={durationMinutes}
            status={slotAvailability[slot.id]?.status}
            conflicts={slotAvailability[slot.id]?.conflicts || []}
            onStatusChange={status =>
              onChange(slot.id, status, slotAvailability[slot.id]?.conflicts)
            }
            onConflictsChange={conflicts =>
              onChange(slot.id, slotAvailability[slot.id]?.status || 'partial', conflicts)
            }
          />
        ))}
      </Stack>
    </Card>
  );
}
